// After a new version is published, an open tab may ask for pieces that were
// renamed. Reload once (not in a loop) so people never see an error for it.
window.addEventListener("vite:preloadError", (event) => {
  const key = "carno:reloaded-for-update";
  if (sessionStorage.getItem(key) === "1") return;
  sessionStorage.setItem(key, "1");
  event.preventDefault();
  window.location.reload();
});
window.addEventListener("load", () => setTimeout(() => sessionStorage.removeItem("carno:reloaded-for-update"), 10_000));

import "@fontsource-variable/inter";
import "./styles.css";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ErrorPage } from "./components/pages/error-page";
import { NotFoundPage } from "./components/pages/not-found-page";
import { api, isApiError, isTransientError } from "./lib/api";
import { connectionState, subscribeConnection } from "./lib/online";
import { initTheme } from "./lib/theme";
import { routeTree } from "./routeTree.gen";

initTheme();

/** Any 401 while signed in means the session ended: go sign in again, then come back. */
function onAuthError(error: unknown): void {
  if (isApiError(error) && error.status === 401 && !window.location.pathname.startsWith("/signin")) {
    const next = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/signin?next=${encodeURIComponent(next)}`);
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onAuthError }),
  mutationCache: new MutationCache({ onError: onAuthError }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Transient trouble (no network, Carno restarting) is retried quietly
      // forever with backoff; other errors twice; 4xx answers are final.
      retry: (count, error) => (isTransientError(error) ? true : count < 2 && !(isApiError(error) && error.status >= 400 && error.status < 500)),
      retryDelay: (attempt) => Math.min(15_000, 1000 * 2 ** attempt),
    },
    // Writes are never auto-retried (offline writes are disabled instead).
    mutations: { retry: false },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Let TanStack Query own freshness.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: ErrorPage,
});

/**
 * While requests are failing, ping a cheap public route every few seconds; as
 * soon as Carno answers, the banner clears and anything that errored refetches.
 */
function watchRecovery(): void {
  let timer: ReturnType<typeof setInterval> | null = null;
  let previous = connectionState();
  subscribeConnection(() => {
    const now = connectionState();
    if (now === "reconnecting" && timer === null) {
      timer = setInterval(() => {
        api.getAuthOptions().catch(() => undefined);
      }, 4000);
    }
    if (now !== "reconnecting" && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    if (previous !== "online" && now === "online") {
      void queryClient.refetchQueries({ type: "active", predicate: (q) => q.state.status === "error" || q.state.fetchFailureCount > 0 });
      void router.invalidate();
    }
    previous = now;
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

async function start(): Promise<void> {
  watchRecovery();
  if (import.meta.env.VITE_MOCK_API === "1") {
    const { installMockApi } = await import("./mocks/install");
    installMockApi();
  }
  const rootElement = document.getElementById("root");
  if (rootElement === null) throw new Error("Missing #root element");
  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
  if (import.meta.env.DEV) {
    void import("./lib/vitals").then((m) => m.reportWebVitals());
  }
}

void start();
